import React from 'react';
import { Circle, CircleCheck, Clock, CreditCard, Diamond, Hourglass, Minus, Square, SquareCheck, Triangle, X } from 'lucide-react';
import type { StatutReservation } from '../../lib/reservationsApi';

/**
 * Statuts du livre (maquette L06 — symbole agrandi, retouche demandée à Claude Design). Le statut n'est JAMAIS porté par la
 * couleur seule : forme (≥ 16 px) + libellé. Seuls les statuts que le serveur connaît sont proposés ; « Reconfirmation envoyée »,
 * « Message laissé », « Numéro erroné »… viendront avec L07 et L12, quand les messages existeront (pas de bouton sans effet).
 */
type Ton = 'warn' | 'ok' | 'info' | 'neutre' | 'danger';

export const STATUTS_AFFICHES: Record<StatutReservation, { label: string; ton: Ton; Icone: React.ComponentType<{ size?: number; strokeWidth?: number }> }> = {
    a_confirmer: { label: 'À confirmer', ton: 'warn', Icone: Circle },
    confirmee: { label: 'Confirmée', ton: 'ok', Icone: CircleCheck },
    option: { label: 'Sous option', ton: 'warn', Icone: Clock },
    attente_client: { label: 'En attente du client', ton: 'warn', Icone: CreditCard },
    arrivee: { label: 'Arrivée', ton: 'info', Icone: Square },
    terminee: { label: 'Terminée', ton: 'neutre', Icone: SquareCheck },
    walk_in: { label: 'Sans réservation', ton: 'info', Icone: Triangle },
    attente: { label: "Liste d'attente", ton: 'warn', Icone: Diamond },
    annulee: { label: 'Annulée', ton: 'neutre', Icone: Minus },
    no_show: { label: 'Absente', ton: 'danger', Icone: X },
    expiree: { label: 'Option expirée', ton: 'neutre', Icone: Hourglass },
};

const TONS: Record<Ton, string> = {
    warn: 'bg-state-warn-soft text-state-warn',
    ok: 'bg-state-ok-soft text-state-ok',
    info: 'bg-state-info-soft text-state-info',
    neutre: 'bg-surface-soft text-content-muted border border-border',
    danger: 'bg-state-danger-soft text-state-danger',
};

export const StatutPastille: React.FC<{ statut: StatutReservation }> = ({ statut }) => {
    const s = STATUTS_AFFICHES[statut] || STATUTS_AFFICHES.a_confirmer;
    return (
        <span className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[12px] font-semibold whitespace-nowrap ${TONS[s.ton]}`}>
            <s.Icone size={16} strokeWidth={2.2} />{s.label}
        </span>
    );
};

/** Libellé de l'action principale d'une réservation : UN seul bouton selon l'état, le reste est dans le menu « ⋯ ». */
export const ACTION_PRINCIPALE: Partial<Record<StatutReservation, { vers: StatutReservation; label: string }>> = {
    a_confirmer: { vers: 'confirmee', label: 'Confirmer' },
    attente: { vers: 'confirmee', label: 'Confirmer' },
    option: { vers: 'confirmee', label: 'Confirmer' },
    confirmee: { vers: 'arrivee', label: 'Marquer arrivé' },
    arrivee: { vers: 'terminee', label: 'Terminé' },
    walk_in: { vers: 'terminee', label: 'Terminé' },
};

export const STATUTS_FERMES: StatutReservation[] = ['annulee', 'no_show', 'expiree'];
export const PEUT_ANNULER = (s: StatutReservation) => !['annulee', 'no_show', 'terminee', 'expiree'].includes(s);
export const PEUT_ABSENT = (s: StatutReservation) => ['a_confirmer', 'confirmee', 'option', 'attente_client', 'attente'].includes(s);
/** « Absent » se propose une fois l'heure de la réservation passée (comme ZenChef ; c'est ce qui ouvre le prélèvement de l'empreinte). */
export const absentPossible = (r: { statut: StatutReservation; debut: string }) => PEUT_ABSENT(r.statut) && r.debut <= maintenantParis();
export const PEUT_MODIFIER =(s: StatutReservation) => !STATUTS_FERMES.includes(s);

/** Minutes de retard d'une réservation non arrivée, aujourd'hui seulement ; 0 sinon. Seuil réglable à venir (10 min par défaut). */
export const SEUIL_RETARD_MIN = 10;
export const minutesDeRetard = (r: { debut: string; statut: StatutReservation }, maintenantLocal: string): number => {
    if (!['a_confirmer', 'confirmee', 'option', 'attente_client'].includes(r.statut)) return 0;
    const t = (s: string) => Date.parse(`${s}:00Z`);
    const m = Math.floor((t(maintenantLocal) - t(r.debut)) / 60000);
    return m >= SEUIL_RETARD_MIN ? m : 0;
};

/** Heure locale de l'établissement (Europe/Paris), « AAAA-MM-JJTHH:MM ». */
export const maintenantParis = (): string => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date()).replace(' ', 'T').slice(0, 16);

/** Clients installés (arrivés ou de passage) : leur table est occupée jusqu'à l'heure de fin prévue. */
export const INSTALLES: StatutReservation[] = ['arrivee', 'walk_in'];
/**
 * Minutes écoulées depuis l'heure de fin prévue d'une table occupée (0 = c'est l'heure), ou null tant que le temps n'est pas
 * écoulé. Sert l'alerte « table à libérer » : on va voir le client et on lui demande gentiment de libérer la table.
 */
export const minutesApresFin = (r: { fin: string; statut: StatutReservation }, maintenantLocal: string): number | null => {
    if (!INSTALLES.includes(r.statut)) return null;
    const t = (x: string) => Date.parse(`${x}:00Z`);
    const m = Math.floor((t(maintenantLocal) - t(r.fin)) / 60000);
    return m >= 0 ? m : null;
};

/** Horodatage de la base (UTC, « AAAA-MM-JJ HH:MM:SS ») → « jj/mm/aaaa hh:mm » à l'heure de Paris. */
export const dateHeureParis = (utc?: string | null): string => {
    if (!utc) return '';
    const t = Date.parse(utc.replace(' ', 'T') + (/[zZ]|[+-]\d\d:?\d\d$/.test(utc) ? '' : 'Z'));
    if (Number.isNaN(t)) return utc;
    return new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(t)).replace(',', '');
};
